import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import GenericTable from "@/components/ui/GenericTable";


import { useScreenPermission } from "@/hooks/useScreenPermission";
import { getAppVersions, updateAppVersionStatus } from "@/services/offers";
import { Pagination } from "../ui";
import AddMobileUpdate from "./AddMobileUpdate";

export default function MobileUpdatesList() {
    const [loading, setLoading] = useState(false);
    const [appVersions, setAppVersions] = useState<any[]>([]);
    const [editingVersion, setEditingVersion] = useState<any | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const { canCreate, canUpdate } = useScreenPermission("app version");
const [togglingId, setTogglingId] = useState<number | null>(null);

    const fetchAppVersions = async () => {
        try {
            setLoading(true);

            const response = await getAppVersions();

            const responseData = response.data as any;

            if (response.status === 200) {
                setAppVersions(responseData || []);
            }
        } catch (error) {
            toast.error("Failed to fetch app versions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAppVersions();
    }, [currentPage]);

    const handleAdd = () => {
        setEditingVersion(null);
        setIsModalOpen(true);
    };

    const handleEdit = (item: any) => {
        setEditingVersion(item);
        setIsModalOpen(true);
    };

  const handleToggleActive = async (item: any) => {
        if (!item.id || togglingId === item.id) return;

        const newStatus = !item.Is_Active;

        // Optimistic update — flip instantly in UI
        setAppVersions((prev) =>
            prev.map((v) => (v.id === item.id ? { ...v, Is_Active: newStatus } : v))
        );
        setTogglingId(item.id);

        try {
            const response = await updateAppVersionStatus(Number(item.id), { Is_Active: newStatus });
            if (response.status === 200) {
                toast.success(`App Version ${newStatus ? "activated" : "deactivated"} successfully`);
                fetchAppVersions();
            } else {
                // Revert on failure
                setAppVersions((prev) =>
                    prev.map((v) => (v.id === item.id ? { ...v, Is_Active: item.Is_Active } : v))
                );
                toast.error("Failed to update app version status");
            }
        } catch {
            // Revert on error
            setAppVersions((prev) =>
                prev.map((v) => (v.id === item.id ? { ...v, Is_Active: item.Is_Active } : v))
            );
            toast.error("Error updating app version status");
        } finally {
            setTogglingId(null);
        }
    };
    const columns = [
        {
            key: "Version_Code",
            label: "Version Code",
            render: (value: number) => (
                <p className="font-semibold text-gray-800">{value}</p>
            ),
        },
        {
            key: "Version",
            label: "Version",
            render: (value: string) => (
                <p className="text-gray-700">{value}</p>
            ),
        },
        {
            key: "File",
            label: "Url",
            render: (value: string) => {
                if (!value) {
                    return (
                        <span className="text-xs text-gray-400">
                            No File
                        </span>
                    );
                }

                const fileBaseUrl =
                    import.meta.env.VITE_BASE_URL.replace("/api", "");

                const fileUrl = `${fileBaseUrl}${value}`;
                const fileName = value.split("/").pop() || "";
                const extension =
                    fileName.split(".").pop()?.toLowerCase() || "";

                return (
                    <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:underline text-sm"
                    >
                        <span className="uppercase text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            {extension}
                        </span>
                        <span className="truncate max-w-[160px]">
                            {fileName}
                        </span>
                    </a>
                );
            },
        },
          {
      key: "status",
      label: "Status",
      render: (_: any, row: any) => (
        <button
          onClick={() => handleToggleActive(row)}
          disabled={togglingId === row.id}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
            row.Is_Active ? "bg-green-500" : "bg-red-500"
          }`}
          title={row.Is_Active ? "Active - Click to deactivate" : "Inactive - Click to activate"}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              row.Is_Active ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      ),
    },
    ];

    return (
        <>
            <div className="space-y-6">
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={20}
                    onPageChange={(page) => setCurrentPage(page)}
                />
                <GenericTable
                    title="App Version"
                    buttonName={canCreate ? "Add App Version" : ""}
                    handleAdd={canCreate ? handleAdd : undefined}
                    columns={columns}
                    data={appVersions}
                    loading={loading}
                    onEdit={canUpdate ? handleEdit : undefined}
                    emptyMessage="No app versions found"
                />
            </div>

            {isModalOpen && (
                <AddMobileUpdate
                    key={editingVersion?.id || "new"}
                    appVersion={editingVersion}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingVersion(null);
                    }}
                    onSuccess={() => {
                        setIsModalOpen(false);
                        fetchAppVersions();
                    }}
                />
            )}
        </>
    );
}